import {compactCssPropertyList} from './CssShorthandUtils';
import {
  CssProperty,
  cssToPropertyList,
} from '@packages/systems/core/utils/CssStringUtils';
import {
  CSS_PROPERTY_ORDER_MAP,
  sortCssProperties,
} from '@packages/systems/style-legacy/css-properties';
import {Section} from '@packages/systems/style/properties';
import {containsCssCustomValue} from '@packages/systems/style/raw-values';

const UNKNOWN_GROUP = 'unknown';

/**
 * Takes a semicolon separated list of CSS definitions and format them into a human-readable pretty format.
 * @param  {String}   cssText     The original CSS text, e.g. 'font-weight: bold; text-align: left; border-left-top-radius: 3px; ...'
 * @param  {Function} hasRawValue Optional predicate function used to determine if a property contains a Custom CSS value (@raw<|...|>)
 * @return {String}               The cleaned up CSS text, e.g. 'border-radius: 3px;\ntext-align: left;\nfont-weight: bold;'
 */
export function beautifyCss(
  cssText: string,
  hasRawValue: (item: CssProperty, index: number) => boolean = (prop) =>
    typeof prop.value === 'string' && containsCssCustomValue(prop.value)
) {
  if (!cssText || cssText.trim().length === 0) {
    return '';
  }

  let properties: CssProperty[] = cssToPropertyList(cssText);

  // If we find any raw values, we can't "compact" our properties, since some
  // shorthand values may be set, which breaks expectations made in
  // `compactCssPropertyList`
  if (!properties.some(hasRawValue)) {
    // Collapse multi-line definitions into shorthand (e.g. 'margin-top: 4px; margin-left: 5px; margin-bottom: 4px; margin-right: 5px;' -> 'margin: 4px 5px;')
    properties = compactCssPropertyList(properties);
  }

  // Sort properties by logical order
  sortCssProperties(properties, 'property', hasRawValue);

  const totalProps = properties.length;
  const uniques = new Set();
  // @ts-expect-error - TS7034 - Variable 'group' implicitly has type 'any' in some locations where its type cannot be determined.
  let group = null;
  const buf: Array<any | string> = [];

  // Write out properties to separate lines
  properties.forEach(function (prop) {
    // Some properties are doubled up due to vendor prefixes, so ignore those we've seen before
    if (uniques.has(prop.property)) {
      return;
    } else {
      uniques.add(prop.property);
    }

    // If there are a lot of properties, break them up into logical sections
    if (totalProps > 5) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'CssPropertyOrderMap'.
      const orderProp = CSS_PROPERTY_ORDER_MAP[prop.property];

      if (!orderProp && !prop.property.startsWith('--')) {
        console.error('Unknown CSS prop: %o', prop.property);
      }

      const nextGroup = prop.property.startsWith('--')
        ? Section.EXPANDED_CSS
        : orderProp?.group
          ? orderProp.group
          : UNKNOWN_GROUP;

      // @ts-expect-error - TS7005 - Variable 'group' implicitly has an 'any' type.
      if (nextGroup !== group) {
        // @ts-expect-error - TS7005 - Variable 'group' implicitly has an 'any' type.
        if (group !== null) {
          buf.push('\n');
        }

        group = nextGroup;
      }
    }

    buf.push(prop.property + ': ' + String(prop.value) + ';\n');
    if (
      prop.property === 'background-clip' &&
      !uniques.has('-webkit-text-fill-color')
    ) {
      if (prop.value === 'text') {
        buf.push('-webkit-text-fill-color: transparent;\n');
      } else {
        buf.push('-webkit-text-fill-color: inherit;\n');
      }
    }
  });

  const cleanText = buf.join('').trim();
  return cleanText;
}
